import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  deleteCurrentUserMessageApi,
  listCurrentUserMessagesByInterlocutorApi,
  markCurrentUserConversationAsReadApi,
  sendCurrentUserTypingSignalApi,
  sendCurrentUserMessageApi,
} from "../../api/chatApi";
import { ChatThreadItem, markChatThreadAsReadLocal } from "../../features/auth/authSlice";
import type { AppDispatch, RootState } from "../../store/store";
import { useTranslation } from "react-i18next";
import styles from "./Messages.module.css";
import ChatMessageContent from "../../components/ChatMessageContent/ChatMessageContent";
import { toast } from "react-toastify";

type UiMessage = {
  id: string;
  content: string;
  isMine: boolean;
  createdAt: string;
  readAt?: string;
};

type ChatItem =
  | { type: "date"; id: string; label: string }
  | { type: "message"; id: string; message: UiMessage };

const CHAT_MESSAGES_PAGE_SIZE = 20;

const createClientMessageId = (): string => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const capitalizeDisplayName = (value: string) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const MY_AVATAR_URI = `data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 88 88'><defs><linearGradient id='g1' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#2f80ed'/><stop offset='100%' stop-color='#1b3e7a'/></linearGradient></defs><rect width='88' height='88' rx='44' fill='url(#g1)'/><circle cx='44' cy='31' r='15' fill='#e8f2ff'/><path d='M18 74c0-14 12-22 26-22s26 8 26 22' fill='#cfe4ff'/><circle cx='33' cy='30' r='2.5' fill='#1f4f93'/><circle cx='55' cy='30' r='2.5' fill='#1f4f93'/></svg>"
)}`;

const OTHER_AVATAR_URI = `data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 88 88'><defs><linearGradient id='g2' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#16a085'/><stop offset='100%' stop-color='#0e5f53'/></linearGradient></defs><rect width='88' height='88' rx='44' fill='url(#g2)'/><circle cx='44' cy='31' r='15' fill='#e8fff8'/><path d='M18 74c0-14 12-22 26-22s26 8 26 22' fill='#c7f3e7'/><path d='M26 25c4-7 10-10 18-10s14 3 18 10' stroke='#0d5f53' stroke-width='4' fill='none' stroke-linecap='round'/></svg>"
)}`;

const MessagesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { user, chatThreads, chatReadByInterlocutor, chatTypingByInterlocutor, latestChatMessage, latestDeletedChatMessage } = useSelector((state: RootState) => state.auth);
  const [selectedThread, setSelectedThread] = useState<ChatThreadItem | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [deleteCandidate, setDeleteCandidate] = useState<UiMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null);
  const typingStopTimerRef = useRef<number | null>(null);
  const sendScrollIntervalRef = useRef<number | null>(null);
  const typingActiveRef = useRef(false);
  const sendingRef = useRef(false);
  const clientMessageIdRef = useRef<string | null>(null);
  const pendingAutoScrollRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const preserveScrollRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const nearBottomRef = useRef(true);
  const forceAutoScrollUntilRef = useRef(0);
  const lockBottomUntilRef = useRef(0);
  const [typingClock, setTypingClock] = useState(Date.now());

  const queryInterlocutorId = String(searchParams.get("with") || "").trim();
  const stateInterlocutorName = String((location.state as any)?.interlocutorName || "").trim();
  const myDisplayName = capitalizeDisplayName(String(
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "Moi"
  ));

  useEffect(() => {
    if (!user) {
      navigate("/register");
    }
  }, [navigate, user]);

  const resolvedThread = useMemo(() => {
    if (queryInterlocutorId) {
      const found = chatThreads.find((item) => item.interlocutorId === queryInterlocutorId);
      if (found) return found;
      return {
        interlocutorId: queryInterlocutorId,
        interlocutorName: stateInterlocutorName || "Utilisateur",
        lastMessage: "",
        lastMessageAt: new Date().toISOString(),
        unreadCount: 0,
      } as ChatThreadItem;
    }
    return chatThreads[0] || null;
  }, [chatThreads, queryInterlocutorId, stateInterlocutorName]);

  useEffect(() => {
    setSelectedThread((prev) => {
      if (!resolvedThread) return null;
      if (!prev) return resolvedThread;
      if (prev.interlocutorId !== resolvedThread.interlocutorId) return resolvedThread;
      return { ...prev, ...resolvedThread };
    });
  }, [resolvedThread]);

  useEffect(() => {
    if (!selectedThread?.interlocutorId) {
      setMessages([]);
      setHasMoreMessages(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setHasMoreMessages(false);
      preserveScrollRef.current = null;
      pendingAutoScrollRef.current = true;
      try {
        const response = await listCurrentUserMessagesByInterlocutorApi(selectedThread.interlocutorId, {
          limit: CHAT_MESSAGES_PAGE_SIZE,
        });
        const incoming = Array.isArray(response?.data?.data) ? response.data.data : [];
        setMessages(
          incoming.map((item: any) => ({
            id: String(item?.id || `${item?.createdAt || Date.now()}`),
            content: typeof item?.content === "string" ? item.content : "",
            isMine: Boolean(item?.isMine),
            createdAt: typeof item?.createdAt === "string" ? item.createdAt : new Date().toISOString(),
            readAt: typeof item?.readAt === "string" ? item.readAt : undefined,
          }))
        );
        setHasMoreMessages(Boolean(response?.data?.hasMore));
        dispatch(markChatThreadAsReadLocal({ interlocutorId: selectedThread.interlocutorId }));
        await markCurrentUserConversationAsReadApi(selectedThread.interlocutorId);
      } catch (_error) {
        setMessages([]);
        setHasMoreMessages(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dispatch, selectedThread?.interlocutorId]);

  const scrollChatToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior,
    });
  }, []);

  const forceScrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    scrollChatToBottom(behavior);
    window.requestAnimationFrame(() => scrollChatToBottom(behavior));
    window.setTimeout(() => scrollChatToBottom(behavior), 80);
  }, [scrollChatToBottom]);

  const forceAnchorToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    if (bottomAnchorRef.current) {
      bottomAnchorRef.current.scrollIntoView({ behavior, block: "end" });
    }
    forceScrollToBottom(behavior);
    window.setTimeout(() => {
      if (bottomAnchorRef.current) {
        bottomAnchorRef.current.scrollIntoView({ behavior: "auto", block: "end" });
      }
      scrollChatToBottom("auto");
    }, 40);
    window.setTimeout(() => {
      if (bottomAnchorRef.current) {
        bottomAnchorRef.current.scrollIntoView({ behavior: "auto", block: "end" });
      }
      scrollChatToBottom("auto");
    }, 140);
  }, [forceScrollToBottom, scrollChatToBottom]);

  const loadOlderMessages = useCallback(async () => {
    const interlocutorId = String(selectedThread?.interlocutorId || "");
    if (!interlocutorId || !hasMoreMessages || loadingOlderRef.current || loading || messages.length === 0) return;
    const oldestMessage = messages[0];
    if (!oldestMessage?.createdAt || !oldestMessage?.id) return;
    const body = bodyRef.current;
    preserveScrollRef.current = body
      ? { scrollHeight: body.scrollHeight, scrollTop: body.scrollTop }
      : null;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const response = await listCurrentUserMessagesByInterlocutorApi(interlocutorId, {
        limit: CHAT_MESSAGES_PAGE_SIZE,
        before: oldestMessage.createdAt,
        beforeId: oldestMessage.id,
      });
      const incoming = Array.isArray(response?.data?.data) ? response.data.data : [];
      const mapped = incoming.map((item: any) => ({
        id: String(item?.id || `${item?.createdAt || Date.now()}`),
        content: typeof item?.content === "string" ? item.content : "",
        isMine: Boolean(item?.isMine),
        createdAt: typeof item?.createdAt === "string" ? item.createdAt : new Date().toISOString(),
        readAt: typeof item?.readAt === "string" ? item.readAt : undefined,
      }));
      setHasMoreMessages(Boolean(response?.data?.hasMore));
      setMessages((prev) => {
        const existingIds = new Set(prev.map((message) => message.id));
        const olderMessages = mapped.filter((message) => !existingIds.has(message.id));
        return [...olderMessages, ...prev].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      });
    } catch (_error) {
      preserveScrollRef.current = null;
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [hasMoreMessages, loading, messages, selectedThread?.interlocutorId]);

  useLayoutEffect(() => {
    const preserved = preserveScrollRef.current;
    const body = bodyRef.current;
    if (!preserved || !body) return;
    body.scrollTop = body.scrollHeight - preserved.scrollHeight + preserved.scrollTop;
    preserveScrollRef.current = null;
  }, [messages.length]);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "0px";
    const nextHeight = Math.min(textarea.scrollHeight, 140);
    textarea.style.height = `${nextHeight}px`;
  }, []);

  useEffect(() => {
    if (!messages.length) return;
    if (preserveScrollRef.current) return;
    const shouldForceByTime = Date.now() < forceAutoScrollUntilRef.current;
    const shouldAutoScroll = pendingAutoScrollRef.current || nearBottomRef.current || shouldForceByTime;
    if (!shouldAutoScroll) return;
    forceAnchorToBottom(pendingAutoScrollRef.current ? "smooth" : "auto");
    pendingAutoScrollRef.current = false;
  }, [forceAnchorToBottom, messages]);

  useLayoutEffect(() => {
    if (Date.now() <= lockBottomUntilRef.current) {
      forceAnchorToBottom("auto");
    }
  }, [forceAnchorToBottom, messages.length]);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [draft, resizeTextarea]);

  useEffect(() => {
    if (!selectedThread?.interlocutorId) return;
    nearBottomRef.current = true;
    forceAutoScrollUntilRef.current = Date.now() + 1500;
  }, [selectedThread?.interlocutorId]);

  useEffect(() => {
    const timer = window.setInterval(() => setTypingClock(Date.now()), 600);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (typingStopTimerRef.current) {
        window.clearTimeout(typingStopTimerRef.current);
        typingStopTimerRef.current = null;
      }
      if (sendScrollIntervalRef.current) {
        window.clearInterval(sendScrollIntervalRef.current);
        sendScrollIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const interlocutorId = String(selectedThread?.interlocutorId || "");
    if (!interlocutorId) return;
    const readAt = chatReadByInterlocutor[interlocutorId];
    if (!readAt) return;
    const readTime = new Date(readAt).getTime();
    setMessages((prev) => prev.map((message) => (
      message.isMine &&
      !message.readAt &&
      !Number.isNaN(readTime) &&
      message.createdAt &&
      new Date(message.createdAt).getTime() <= readTime
        ? { ...message, readAt }
        : message
    )));
  }, [chatReadByInterlocutor, selectedThread?.interlocutorId]);

  useEffect(() => {
    const interlocutorId = String(selectedThread?.interlocutorId || "");
    if (!latestChatMessage?.id || latestChatMessage.interlocutorId !== interlocutorId) return;
    const shouldAutoScroll = latestChatMessage.isMine || nearBottomRef.current;

    setMessages((prev) => {
      const existingIndex = prev.findIndex((message) => message.id === latestChatMessage.id);
      const nextMessage: UiMessage = {
        id: latestChatMessage.id,
        content: latestChatMessage.content,
        isMine: latestChatMessage.isMine,
        createdAt: latestChatMessage.createdAt,
        readAt: latestChatMessage.readAt,
      };

      if (existingIndex !== -1) {
        return prev.map((message, index) => (
          index === existingIndex ? { ...message, ...nextMessage } : message
        ));
      }

      return [...prev, nextMessage].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    });

    if (shouldAutoScroll) {
      pendingAutoScrollRef.current = true;
    }

    if (!latestChatMessage.isMine) {
      dispatch(markChatThreadAsReadLocal({ interlocutorId: latestChatMessage.interlocutorId }));
      markCurrentUserConversationAsReadApi(latestChatMessage.interlocutorId).catch(() => undefined);
    }
  }, [dispatch, latestChatMessage, selectedThread?.interlocutorId]);

  useEffect(() => {
    const interlocutorId = String(selectedThread?.interlocutorId || "");
    if (!latestDeletedChatMessage?.messageId || latestDeletedChatMessage.interlocutorId !== interlocutorId) return;
    setMessages((prev) => prev.filter((message) => message.id !== latestDeletedChatMessage.messageId));
    setDeleteCandidate((prev) => (
      prev?.id === latestDeletedChatMessage.messageId ? null : prev
    ));
  }, [latestDeletedChatMessage, selectedThread?.interlocutorId]);

  const handleSelectThread = (thread: ChatThreadItem) => {
    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }
    if (typingActiveRef.current && selectedThread?.interlocutorId) {
      typingActiveRef.current = false;
      sendCurrentUserTypingSignalApi({ toUserId: selectedThread.interlocutorId, isTyping: false }).catch(() => undefined);
    }
    setSelectedThread(thread);
    setDeleteCandidate(null);
    navigate(`/messages?with=${encodeURIComponent(thread.interlocutorId)}`, { replace: true });
  };

  const handleSend = async () => {
    if (!selectedThread?.interlocutorId) return;
    if (sendingRef.current) return;
    const content = draft.trim();
    if (!content) return;
    sendingRef.current = true;
    const clientMessageId = clientMessageIdRef.current || createClientMessageId();
    clientMessageIdRef.current = clientMessageId;
    pendingAutoScrollRef.current = true;
    nearBottomRef.current = true;
    forceAutoScrollUntilRef.current = Date.now() + 3000;
    lockBottomUntilRef.current = Date.now() + 2200;
    if (sendScrollIntervalRef.current) {
      window.clearInterval(sendScrollIntervalRef.current);
      sendScrollIntervalRef.current = null;
    }
    const lockUntil = Date.now() + 1100;
    sendScrollIntervalRef.current = window.setInterval(() => {
      forceAnchorToBottom("auto");
      if (Date.now() >= lockUntil && sendScrollIntervalRef.current) {
        window.clearInterval(sendScrollIntervalRef.current);
        sendScrollIntervalRef.current = null;
      }
    }, 45);
    setSending(true);
    try {
      await sendCurrentUserMessageApi({
        toUserId: selectedThread.interlocutorId,
        content,
        clientMessageId,
      });
      setDraft("");
      forceAnchorToBottom("smooth");
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        if (selectedThread?.interlocutorId) {
          sendCurrentUserTypingSignalApi({ toUserId: selectedThread.interlocutorId, isTyping: false }).catch(() => undefined);
        }
      }
    } finally {
      sendingRef.current = false;
      clientMessageIdRef.current = null;
      setSending(false);
      if (sendScrollIntervalRef.current) {
        window.clearInterval(sendScrollIntervalRef.current);
        sendScrollIntervalRef.current = null;
      }
    }
  };

  const handleChatBodyScroll = () => {
    const body = bodyRef.current;
    if (!body) return;
    const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
    nearBottomRef.current = distanceFromBottom <= 40;
    if (body.scrollTop <= 48) {
      void loadOlderMessages();
    }
  };

  const handleDraftChange = (value: string) => {
    setDraft(value);
    const interlocutorId = selectedThread?.interlocutorId;
    if (!interlocutorId) return;

    const hasText = value.trim().length > 0;
    if (hasText && !typingActiveRef.current) {
      typingActiveRef.current = true;
      sendCurrentUserTypingSignalApi({ toUserId: interlocutorId, isTyping: true }).catch(() => undefined);
    }

    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }

    if (!hasText) {
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        sendCurrentUserTypingSignalApi({ toUserId: interlocutorId, isTyping: false }).catch(() => undefined);
      }
      return;
    }

    typingStopTimerRef.current = window.setTimeout(() => {
      const activeInterlocutorId = selectedThread?.interlocutorId;
      if (!activeInterlocutorId || !typingActiveRef.current) return;
      typingActiveRef.current = false;
      sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: false }).catch(() => undefined);
    }, 1400);
  };

  const handleConfirmDeleteMessage = async () => {
    if (!deleteCandidate?.id) return;
    setDeleting(true);
    try {
      await deleteCurrentUserMessageApi(deleteCandidate.id);
      setMessages((prev) => prev.filter((item) => item.id !== deleteCandidate.id));
      setDeleteCandidate(null);
      toast.success(t("header.chatDeleteSuccess"));
    } catch (_error) {
      toast.error(t("header.chatDeleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const activeInterlocutorId = String(selectedThread?.interlocutorId || "");
  const isInterlocutorTyping = Boolean(
    activeInterlocutorId &&
    Number(chatTypingByInterlocutor[activeInterlocutorId] || 0) > typingClock
  );

  useEffect(() => {
    if (!isInterlocutorTyping) return;
    nearBottomRef.current = true;
    pendingAutoScrollRef.current = true;
    forceAnchorToBottom("smooth");
  }, [forceAnchorToBottom, isInterlocutorTyping]);

  const myAvatar = MY_AVATAR_URI;
  const interlocutorName = capitalizeDisplayName(String(selectedThread?.interlocutorName || "Utilisateur"));
  const interlocutorAvatar = OTHER_AVATAR_URI;
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const locale = i18n.resolvedLanguage || "fr";

  const formatMessageTime = useCallback((value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsed);
  }, [locale]);

  const formatMessageDateSeparator = useCallback((value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(parsed).toUpperCase();
  }, [locale]);

  const chatItems = useMemo<ChatItem[]>(() => {
    const items: ChatItem[] = [];
    let previousDateKey = "";

    messages.forEach((message) => {
      const parsed = new Date(message.createdAt);
      const dateKey = Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);

      if (dateKey && dateKey !== previousDateKey) {
        items.push({
          type: "date",
          id: `date-${dateKey}`,
          label: formatMessageDateSeparator(message.createdAt),
        });
        previousDateKey = dateKey;
      }

      items.push({
        type: "message",
        id: message.id,
        message,
      });
    });

    return items;
  }, [formatMessageDateSeparator, messages]);

  return (
    <div className={styles.page}>
      <main className={styles.layout}>
        <aside className={styles.sidebar}>
          <h2>{t("header.messagesTitle")}</h2>
          <div className={styles.threadList}>
            {chatThreads.length === 0 ? (
              <div className={styles.empty}>{t("header.messagesEmpty")}</div>
            ) : (
              chatThreads.map((thread) => (
                <button
                  type="button"
                  key={thread.interlocutorId}
                  onClick={() => handleSelectThread(thread)}
                  className={`${styles.threadItem} ${selectedThread?.interlocutorId === thread.interlocutorId ? styles.threadItemActive : ""} ${thread.unreadCount > 0 ? styles.threadUnread : ""}`}
                >
                  <img
                    src={OTHER_AVATAR_URI}
                    alt={capitalizeDisplayName(thread.interlocutorName)}
                    className={styles.threadAvatar}
                    loading="lazy"
                  />
                  <span className={styles.threadMeta}>
                    <strong>{capitalizeDisplayName(thread.interlocutorName)}</strong>
                    <span>{thread.lastMessage || t("header.chatNoMessage")}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        <section className={styles.chatPanel}>
          <header className={styles.chatHeader}>
            <h3>{selectedThread?.interlocutorName || t("header.messagesTitle")}</h3>
          </header>
          <div ref={bodyRef} className={styles.chatBody} onScroll={handleChatBodyScroll}>
            {loading && messages.length === 0 ? (
              <div className={styles.empty}>{t("header.chatLoading")}</div>
            ) : messages.length === 0 ? (
              <div className={styles.empty}>{t("header.chatNoMessage")}</div>
            ) : (
              <>
              {loadingOlder && <div className={styles.historyLoader}>{t("header.chatLoading")}</div>}
              {chatItems.map((item) => {
                if (item.type === "date") {
                  return (
                    <div key={item.id} className={styles.dateSeparator}>
                      <span>{item.label}</span>
                    </div>
                  );
                }

                const message = item.message;
                const senderName = message.isMine ? myDisplayName : interlocutorName;
                const sentAt = formatMessageTime(message.createdAt);
                const showMessageSeen = Boolean(
                  message.isMine &&
                  lastMessage?.id === message.id &&
                  message.readAt
                );

                return (
                  <div
                    key={message.id}
                    className={`${styles.messageRow} ${message.isMine ? styles.messageRowMine : styles.messageRowTheirs}`}
                  >
                    <div className={`${styles.messageTopRow} ${message.isMine ? styles.messageTopRowMine : styles.messageTopRowTheirs}`}>
                      {!message.isMine && (
                        <img src={interlocutorAvatar} alt={interlocutorName} className={styles.avatar} loading="lazy" />
                      )}
                      {message.isMine && (
                        <button
                          type="button"
                          className={styles.messageMenuButton}
                          aria-label={t("header.chatDeleteMenuAria")}
                          onClick={() => setDeleteCandidate(message)}
                        >
                          <span aria-hidden="true">?</span>
                        </button>
                      )}
                      <div className={`${styles.messageMeta} ${message.isMine ? styles.messageMetaMine : styles.messageMetaTheirs}`}>
                        {message.isMine ? (
                          <>
                            {sentAt ? <span>{sentAt}</span> : null}
                            <strong>{senderName}</strong>
                          </>
                        ) : (
                          <>
                            <strong>{senderName}</strong>
                            {sentAt ? <span>{sentAt}</span> : null}
                          </>
                        )}
                      </div>
                      {message.isMine && (
                        <img src={myAvatar} alt={myDisplayName} className={styles.avatar} loading="lazy" />
                      )}
                    </div>
                    <div className={styles.messageContent}>
                      <div className={`${styles.bubble} ${message.isMine ? styles.mine : styles.theirs}`}>
                        <ChatMessageContent content={message.content} />
                      </div>
                      {showMessageSeen && <div className={styles.seen}>{t("header.chatSeen")}</div>}
                    </div>
                  </div>
                );
              })}
              </>
            )}
            {isInterlocutorTyping && (
              <div className={`${styles.messageRow} ${styles.messageRowTheirs}`}>
                <img src={interlocutorAvatar} alt={interlocutorName} className={styles.avatar} loading="lazy" />
                <div className={styles.typing} aria-live="polite" aria-label={t("header.chatTyping")}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={bottomAnchorRef} aria-hidden="true" />
          </div>
          <div className={styles.chatComposer}>
            <textarea
              ref={textareaRef}
              rows={1}
              value={draft}
              onChange={(event) => handleDraftChange(event.target.value)}
              className={styles.input}
              placeholder={t("header.chatPlaceholder")}
            />
            <button
              type="button"
              className={styles.send}
              disabled={sending || !draft.trim()}
              onClick={handleSend}
              aria-label={t("header.chatSend")}
              title={t("header.chatSend")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M3 20L21 12L3 4L3.01 10L16 12L3.01 14L3 20Z" fill="currentColor" />
              </svg>
            </button>
          </div>
          {deleteCandidate && (
            <div className={styles.deleteModalOverlay} onClick={() => setDeleteCandidate(null)}>
              <div className={styles.deleteModal} onClick={(event) => event.stopPropagation()}>
                <p className={styles.deleteModalTitle}>{t("header.chatDeleteConfirmTitle")}</p>
                <p className={styles.deleteModalText}>{t("header.chatDeleteConfirmText")}</p>
                <div className={styles.deleteModalActions}>
                  <button
                    type="button"
                    className={styles.deleteCancel}
                    disabled={deleting}
                    onClick={() => setDeleteCandidate(null)}
                  >
                    {t("header.chatDeleteCancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.deleteConfirm}
                    disabled={deleting}
                    onClick={handleConfirmDeleteMessage}
                  >
                    {deleting ? t("header.chatDeleteLoading") : t("header.chatDeleteConfirm")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default MessagesPage;
